import { TCreateConverter, TFetchConverter } from 'data/types/converter.types';
import {
  EAgeGroup,
  ELanguage,
  EWorkshopPartType,
  EWorkshopType,
} from 'data/types/generic.types';
import {
  TAllAttendanceListDataDB,
  TAllAttendanceListDataUI,
  TAttendanceDB,
  TAttendanceUI,
  TAttendanceUpdateDB,
  TAttendanceUpdateUI,
  TChildCareRequirementsDB,
  TChildCareRequirementsUI,
  TGetChildCareDataDB,
  TGetChildCareDataUI,
  TGlobalChildcareCountsDB,
  TGlobalChildcareCountsUI,
  TRegistrantDB,
  TRegistrantUI,
  TSessionAttendanceListDataDB,
  TSessionAttendanceListDataUI,
  TSessionSubmitDB,
  TSessionSubmitUI,
  TEventAttendanceListDataDB,
  TEventAttendanceListDataUI,
  TEventAttendanceUpdateDB,
  TEventAttendanceUpdateUI,
  TEventRegistrantDB,
  TEventChildCareRequirementsDB,
  TEventChildCareRequirementsUI,
  TGetEventChildCareDataDB,
  TGetEventChildCareDataUI,
} from 'data/types/session.types';
import {
  dateConverter,
  dateShortenFormat,
  dateTimeFormatUI,
} from 'helpers/utils';
import { formatSessionDateTimeRange } from 'helpers/formatSessionDateTimeRange';
import dayjs from 'dayjs';

/**
 * Joins children names for display. Minimal registrants (GROW-4352) never carry
 * children names, so they always resolve to an empty string — the UI then hides
 * the row instead of rendering an empty one.
 */
const joinChildrenNames = (
  names: string[] | null | undefined,
  minimalRegistration: boolean,
): string =>
  minimalRegistration
    ? ''
    : (names || []).filter(name => !!name?.trim()).join(', ');

export const getAttendancesConvertor: TFetchConverter<
  TAttendanceUI,
  TAttendanceDB
> = {
  fromDb: data => {
    return {
      isAttended: data.isAttended,
      attendanceId: data.attendanceId,
      kitCount: data.kitCount,
      isUpdatable: data.isUpdatable,
    };
  },
};

export const getRegistrantConverter = (
  workshopType: EWorkshopType,
): TFetchConverter<
  TRegistrantUI & { workshopId: string; isSessionStarted: boolean },
  TRegistrantDB & { workshopId: string; isSessionStarted: boolean }
> => ({
  fromDb: data => {
    const minimalRegistration = !!data.minimalRegistration;
    const childrenNames = joinChildrenNames(
      data.childrenNames,
      minimalRegistration,
    );

    return {
      workshopId: data.workshopId,
      kitsOrdered: data.kitsOrdered,
      language: data.language,
      lastName: data.lastName,
      orientationKitRequested: data.orientationKitRequested,
      firstName: data.firstName,
      partType: data.partType,
      ageGroup: data.ageGroup,
      materialsLanguage: data.materialsLanguage,
      attendance: getAttendancesConvertor.fromDb(data.attendance),
      registrantId: data.registrantId,
      registrationDate: dateConverter(data.registrationDate, dateShortenFormat),
      childrenNames,
      orientationKitReceived: data.orientationKitReceived,
      workshopClassId: data.workshopClassId,
      showOrientationKit:
        data.partType === EWorkshopPartType.PART_ONE &&
        workshopType === EWorkshopType.READY_FOR_KINDERGARTEN,
      globalSearchString: (data.firstName || '')
        .concat(data.lastName || '')
        .concat(childrenNames)
        .toLowerCase(),
      allowedTakePicture: !!data.allowedTakePicture,
      minimalRegistration,
      isSessionStarted: data.isSessionStarted,
    };
  },
});

export const getSessionAttendanceListDataConverter: TFetchConverter<
  TSessionAttendanceListDataUI,
  TSessionAttendanceListDataDB
> = {
  fromDb: data => {
    const isCCP = [
      EWorkshopType.CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_1,
      EWorkshopType.CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_2,
      EWorkshopType.LEARNING_CHILD_CARE_AND_PRESCHOOL_PROGRAMS,
    ].includes(data.workshopType);

    // This endpoint carries the start in sessionDate, not sessionDateTime.
    const formattedDateTime = formatSessionDateTimeRange(
      data.sessionDateTime ?? data.sessionDate,
      data.sessionEndDate,
      data.timezone,
    );

    return {
      language: data.language,
      hostName: data.hostName,
      workshopType: data.workshopType,
      ageGroup: data.ageGroup,
      workshopClassId: data.workshopClassId,
      attendanceType: data.attendanceType,
      sessionId: data.sessionId,
      workshopPartType: data.workshopPartType,
      sessionDate: dateConverter(data.sessionDate, dateTimeFormatUI),
      // eslint-disable-next-line prettier/prettier
      sessionDateTime:
        // eslint-disable-next-line prettier/prettier
        formattedDateTime || dateConverter(data.sessionDate, dateTimeFormatUI),
      registrants: (data.registrants || []).map(registrant =>
        getRegistrantConverter(data.workshopType).fromDb({
          ...registrant,
          workshopId: data.workshopId,
          isSessionStarted: data.isSessionStarted,
        }),
      ),
      isSessionStarted: data.isSessionStarted,
      isCCP,
      workshopId: data.workshopId,
      attendanceState: data.attendanceState,
      attendanceOpensAt: data.attendanceOpensAt
        ? dayjs(data.attendanceOpensAt).format('MMM D, YYYY h:mm A')
        : undefined,
      attendanceClosesAt: data.attendanceClosesAt
        ? dayjs(data.attendanceClosesAt).format('MMM D, YYYY h:mm A')
        : undefined,
    };
  },
};

export const getAllAttendanceListDataConverter: TFetchConverter<
  TAllAttendanceListDataUI,
  TAllAttendanceListDataDB
> = {
  fromDb: data => {
    const isCCP = [
      EWorkshopType.CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_1,
      EWorkshopType.CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_2,
      EWorkshopType.LEARNING_CHILD_CARE_AND_PRESCHOOL_PROGRAMS,
    ].includes(data.workshopType);

    const formattedDateTime = formatSessionDateTimeRange(
      data.sessionDateTime,
      data.sessionEndDateTime,
      data.timezone,
    );

    return {
      workshopId: data.workshopId,
      hostName: data.hostName,
      workshopType: data.workshopType,
      sessionDate: dateConverter(data.sessionDate, dateTimeFormatUI),
      // eslint-disable-next-line prettier/prettier
      sessionDateTime:
        // eslint-disable-next-line prettier/prettier
        formattedDateTime || dateConverter(data.sessionDate, dateTimeFormatUI),
      registrants: (data.registrants || []).map(registrant =>
        getRegistrantConverter(data.workshopType).fromDb({
          ...registrant,
          workshopId: data.workshopId,
          isSessionStarted: data.isSessionStarted,
        }),
      ),
      isSessionStarted: data.isSessionStarted,
      attendanceType: data.attendanceType,
      isCCP,
      attendanceState: data.attendanceState,
      attendanceOpensAt: data.attendanceOpensAt
        ? dayjs(data.attendanceOpensAt).format('MMM D, YYYY h:mm A')
        : undefined,
      attendanceClosesAt: data.attendanceClosesAt
        ? dayjs(data.attendanceClosesAt).format('MMM D, YYYY h:mm A')
        : undefined,
    };
  },
};

export const getChildCareCountConverter: TFetchConverter<
  TGlobalChildcareCountsUI,
  TGlobalChildcareCountsDB
> = {
  fromDb: data => {
    return {
      name: data.ageGroup,
      value: data.count,
    };
  },
};

export const getChildCareRequirementsConverter: TFetchConverter<
  TChildCareRequirementsUI,
  TChildCareRequirementsDB
> = {
  fromDb: data => {
    return {
      childcareRequirements: (data.childcareRequirements || []).map(item =>
        getChildCareCountConverter.fromDb(item),
      ),
      firstName: data.firstName,
      lastName: data.lastName,
      registrantId: data.registrantId,
      workshopClassId: data.workshopClassId,
    };
  },
};

export const getChildCareListDataConverter: TFetchConverter<
  TGetChildCareDataUI,
  TGetChildCareDataDB
> = {
  fromDb: data => {
    return {
      workshopId: data.workshopId,
      hostName: data.hostName,
      workshopType: data.workshopType,
      location: data.location,
      globalChildcareCounts: (data.globalChildcareCounts || []).map(item =>
        getChildCareCountConverter.fromDb(item),
      ),
      childcareRequirements: (data.childcareRequirements || []).map(item =>
        getChildCareRequirementsConverter.fromDb(item),
      ),
    };
  },
};

export const submitSessionConverter: TCreateConverter<
  TSessionSubmitUI,
  TSessionSubmitDB
> = {
  toDb: data => {
    return {
      sessionId: data.sessionId,
      workshopClassId: data.workshopClassId,
    };
  },
};
export const updateAttendanceConverter: TCreateConverter<
  TAttendanceUpdateUI,
  TAttendanceUpdateDB
> = {
  toDb: data => {
    // reset kit count and orientationKitReceived if the registrant is not attended
    const kitCount = data.isAttended ? data.kitCount || 0 : 0;
    const orientationKitReceived = data.isAttended
      ? data.orientationKitReceived
      : false;
    return {
      attendanceId: data.attendanceId,
      isAttended: data.isAttended,
      registrantId: data.registrantId,
      workshopClassId: data.workshopClassId,
      kitCount,
      orientationKitReceived,
      workshopId: data.workshopId,
    };
  },
};

// ===================== EVENTS (Host App) =====================

export const getEventRegistrantConverter: TFetchConverter<
  TRegistrantUI & { workshopId: string; isSessionStarted: boolean },
  TEventRegistrantDB & { workshopId: string; isSessionStarted: boolean }
> = {
  fromDb: data => {
    const minimalRegistration = !!data.minimalRegistration;
    const childrenNames = joinChildrenNames(
      data.childrenNames,
      minimalRegistration,
    );
    return {
      workshopId: data.workshopId,
      kitsOrdered: false,
      language: ELanguage.ENGLISH,
      lastName: data.lastName,
      orientationKitRequested: false,
      firstName: data.firstName,
      partType: data.partType as EWorkshopPartType,
      ageGroup: EAgeGroup.THREE_TO_FIVE,
      materialsLanguage: ELanguage.ENGLISH,
      attendance: {
        attendanceId: data.attendance.attendanceId,
        isAttended: data.attendance.isAttended,
        kitCount: 0,
        isUpdatable: data.attendance.isUpdatable,
      },
      registrantId: data.registrantId,
      registrationDate: dateConverter(data.registrationDate, dateShortenFormat),
      childrenNames,
      orientationKitReceived: false,
      workshopClassId: data.eventPartId,
      showOrientationKit: false,
      globalSearchString: (data.firstName || '')
        .concat(data.lastName || '')
        .concat(childrenNames)
        .toLowerCase(),
      allowedTakePicture: !!data.allowedTakePicture,
      minimalRegistration,
      isSessionStarted: data.isSessionStarted,
    };
  },
};

export const getEventAttendanceListDataConverter: TFetchConverter<
  TEventAttendanceListDataUI,
  TEventAttendanceListDataDB
> = {
  fromDb: data => {
    const formattedDateTime = data.partDateTime
      ? dayjs(data.partDateTime).format('MMM D, YYYY h:mm A')
      : null;

    return {
      workshopId: data.eventId,
      hostName: data.hostName,
      eventType: data.eventType,
      sessionDate: dateConverter(data.partDate, dateTimeFormatUI),
      sessionDateTime:
        formattedDateTime || dateConverter(data.partDate, dateTimeFormatUI),
      registrants: (data.registrants || []).map(registrant =>
        getEventRegistrantConverter.fromDb({
          ...registrant,
          workshopId: data.eventId,
          isSessionStarted: data.isSessionStarted,
        }),
      ),
      isSessionStarted: data.isSessionStarted,
      attendanceType: data.attendanceType,
      isCCP: false,
      attendanceState: data.attendanceState,
      attendanceOpensAt: data.attendanceOpensAt
        ? dayjs(data.attendanceOpensAt).format('MMM D, YYYY h:mm A')
        : undefined,
      attendanceClosesAt: data.attendanceClosesAt
        ? dayjs(data.attendanceClosesAt).format('MMM D, YYYY h:mm A')
        : undefined,
    };
  },
};

export const updateEventAttendanceConverter: TCreateConverter<
  TEventAttendanceUpdateUI,
  TEventAttendanceUpdateDB
> = {
  toDb: data => {
    return {
      attendanceId: data.attendanceId,
      isAttended: data.isAttended,
      registrantId: data.registrantId,
      eventId: data.eventId,
    };
  },
};

// Event Child Care List Converters
export const getEventChildCareRequirementsConverter: TFetchConverter<
  TEventChildCareRequirementsUI,
  TEventChildCareRequirementsDB
> = {
  fromDb: data => {
    return {
      childcareRequirements: (data.childcareRequirements || []).map(item =>
        getChildCareCountConverter.fromDb(item),
      ),
      firstName: data.firstName,
      lastName: data.lastName,
      registrantId: data.registrantId,
      eventPartId: data.eventPartId,
    };
  },
};

export const getEventChildCareListDataConverter: TFetchConverter<
  TGetEventChildCareDataUI,
  TGetEventChildCareDataDB
> = {
  fromDb: data => {
    return {
      eventId: data.eventId,
      hostName: data.hostName,
      location: data.location,
      eventType: data.eventType,
      globalChildcareCounts: (data.globalChildcareCounts || []).map(item =>
        getChildCareCountConverter.fromDb(item),
      ),
      childcareRequirements: (data.childcareRequirements || []).map(item =>
        getEventChildCareRequirementsConverter.fromDb(item),
      ),
    };
  },
};
